
import { TeacherDocument } from '@/types/teacher.types';
import { AdminDocument } from '@/types/admin.types';
import { notificationManager } from '../NotificationManagerInstance';
import { NotificationPriority } from '@/types/notifications';

export type TrackableDocument = TeacherDocument | AdminDocument;

export interface DocumentExpiryConfig {
  documentType: string;
  warningDays: number[];  // e.g. [90, 30, 7] for notifications at these day marks
  notificationType: 'teacher' | 'admin';
}

export class DocumentTrackingService {
  private static instance: DocumentTrackingService;
  private documentConfigs: DocumentExpiryConfig[] = [
    {
      documentType: 'contract',
      warningDays: [90, 30, 7],
      notificationType: 'teacher'
    },
    {
      documentType: 'passport',
      warningDays: [60, 30, 14],
      notificationType: 'teacher'
    },
    {
      documentType: 'certificate',
      warningDays: [90, 60, 30],
      notificationType: 'teacher'
    }
  ];

  public static getInstance(): DocumentTrackingService {
    if (!DocumentTrackingService.instance) {
      DocumentTrackingService.instance = new DocumentTrackingService();
    }
    return DocumentTrackingService.instance;
  }

  public async checkDocumentExpiry(document: TrackableDocument, userId: string): Promise<void> {
    const config = this.documentConfigs.find(c => c.documentType === document.documentType);
    if (!config) return;

    const expiryDate = document.expirationDate ? new Date(document.expirationDate) : null;
    if (!expiryDate) return;

    const daysUntilExpiry = Math.ceil((expiryDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));

    // Check if we need to send a notification based on warning days
    for (const warningDay of config.warningDays) {
      if (daysUntilExpiry === warningDay) {
        await this.createExpiryNotification(document, userId, daysUntilExpiry, config.notificationType);
      }
    }
  }

  private async createExpiryNotification(
    document: TrackableDocument,
    userId: string,
    daysLeft: number,
    type: 'teacher' | 'admin'
  ): Promise<void> {
    // Map our internal priority values to the NotificationPriority type from types/notifications.ts
    let priority: NotificationPriority;
    if (daysLeft <= 7) {
      priority = 'high';
    } else if (daysLeft <= 30) {
      priority = 'medium';
    } else {
      priority = 'low';
    }
    
    const category = 'document';
    
    const notification = {
      title: `${document.documentType?.charAt(0).toUpperCase()}${document.documentType?.slice(1)} Expiring Soon`,
      message: `Your ${document.documentType} will expire in ${daysLeft} days.`,
      metadata: {
        documentId: document.id,
        documentType: document.documentType,
        expiryDate: document.expirationDate,
        daysLeft,
        actionUrl: `/${type}s/${userId}/documents`,
      }
    };

    await notificationManager.createTeacherNotification(
      notification.title,
      notification.message,
      userId,
      undefined,
      category,
      priority
    );
  }

  public addDocumentConfig(config: DocumentExpiryConfig): void {
    const existingIndex = this.documentConfigs.findIndex(c => c.documentType === config.documentType);
    if (existingIndex >= 0) {
      this.documentConfigs[existingIndex] = config;
    } else {
      this.documentConfigs.push(config);
    }
  }

  public getDocumentConfigs(): DocumentExpiryConfig[] {
    return [...this.documentConfigs];
  }
}

export const documentTrackingService = DocumentTrackingService.getInstance();
